import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight } from 'lucide-react';
interface Slide {
  label: string;
  src: string;
}
const slides: Slide[] = [{
  label: 'Exterior — Daytime',
  src: 'https://cdn.magicpatterns.com/uploads/swGofMvq8y8v1t4CkXV1Ks/18.png'
}, {
  label: 'Street Approach',
  src: 'https://cdn.magicpatterns.com/uploads/obut8pK3yLJq8AWRMVTd61/16.png'
}, {
  label: 'Garden Elevation',
  src: 'https://cdn.magicpatterns.com/uploads/njwjiWx51JyeyeAb8LTUm4/17.png'
}, {
  label: 'Exterior — Twilight',
  src: 'https://cdn.magicpatterns.com/uploads/gaeXncFSPgy4fvWjhpUxjX/20.png'
}];
export function Slideshow() {
  const [index, setIndex] = useState(0);
  const go = useCallback((dir: number) => {
    setIndex(prev => (prev + dir + slides.length) % slides.length);
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') go(-1);
      if (e.key === 'ArrowRight') go(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go]);
  // Slow, gentle auto-advance.
  useEffect(() => {
    const id = setInterval(() => go(1), 6500);
    return () => clearInterval(id);
  }, [go]);
  return <section className="pt-14 md:pt-32 pb-10 md:pb-14 bg-bg-light relative z-10" data-id="element-143">
      <div className="w-full" data-id="element-144">
        <motion.header initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 1,
        ease: [0.16, 1, 0.3, 1]
      }} className="mb-8 md:mb-12 px-6 md:px-14 max-w-2xl" data-id="element-145">
          <p className="text-xs tracking-[0.28em] uppercase text-text-muted font-light mb-4" data-id="element-146">
            Gallery
          </p>
          <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight text-primary leading-tight" data-id="element-147">
            Discover Campanilla
          </h2>
        </motion.header>

        <div className="group relative w-full aspect-[3/4] sm:aspect-[16/10] md:aspect-auto md:h-[82vh] overflow-hidden bg-bg-warm" role="region" aria-roledescription="carousel" aria-label="Property gallery" data-id="element-148">
          <AnimatePresence mode="wait" data-id="element-149">
            <motion.div key={index} initial={{
            opacity: 0,
            scale: 1.03
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 1.1,
            ease: [0.16, 1, 0.3, 1]
          }} className="absolute inset-0" data-id="element-150">
              <img src={slides[index].src} alt={slides[index].label} className="w-full h-full object-cover" data-id="element-151" />
            </motion.div>
          </AnimatePresence>

          {/* arrows */}
          <button onClick={() => go(-1)} aria-label="Previous slide" className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-white/70 backdrop-blur text-primary opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity" data-id="element-152">
            <ArrowLeft className="w-5 h-5" strokeWidth={1} data-id="element-153" />
          </button>
          <button onClick={() => go(1)} aria-label="Next slide" className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-white/70 backdrop-blur text-primary opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity" data-id="element-154">
            <ArrowRight className="w-5 h-5" strokeWidth={1} data-id="element-155" />
          </button>
        </div>

        {/* line indicators */}
        <div className="flex items-center justify-center gap-3 md:gap-4 mt-8" data-id="element-156">
          {slides.map((slide, idx) => <button key={slide.label} onClick={() => setIndex(idx)} className="group/line py-3" aria-label={`Go to slide ${idx + 1}: ${slide.label}`} aria-current={index === idx ? 'true' : undefined} data-id="element-157">
              <span className={`block h-[1px] transition-all duration-700 ease-out ${index === idx ? 'w-12 md:w-16 bg-primary' : 'w-6 md:w-8 bg-black/10 group-hover/line:bg-black/30'}`} data-id="element-158" />
            </button>)}
        </div>

        {/* active area name */}
        <p className="mt-4 text-center text-sm md:text-base font-light tracking-wide text-primary" data-id="element-159">
          {slides[index].label}
        </p>
      </div>
    </section>;
}